import { logUserActivity } from './userActivity';
import type { UserActivityEvent } from '@/types/userActivity';

const pendingLogs = new Map<string, NodeJS.Timeout>();

/**
 * Debounced activity logger — only logs after specified delay of inactivity.
 * Use for rapid-fire actions like date picker or dropdown changes.
 *
 * @param event - The activity event to log
 * @param debounceKey - Unique key to identify this debounce group (e.g., 'date-picker', 'tech-select')
 * @param delayMs - Delay in milliseconds before logging (default: 2000ms)
 *
 * @example
 * // Only logs after user stops changing the date for 2 seconds
 * logUserActivityDebounced({
 *   category: 'route_tracking',
 *   action: 'date_selected',
 *   metadata: { selected_date: '2024-02-05' }
 * }, 'date-picker');
 */
export function logUserActivityDebounced(
  event: UserActivityEvent,
  debounceKey: string,
  delayMs = 2000
): void {
  // Clear any pending log for this key
  const existing = pendingLogs.get(debounceKey);
  if (existing) {
    clearTimeout(existing);
  }

  // Schedule new log after delay
  const timeout = setTimeout(() => {
    pendingLogs.delete(debounceKey);
    logUserActivity(event);
  }, delayMs);

  pendingLogs.set(debounceKey, timeout);
}

/**
 * Cancel a pending debounced log without firing it.
 * Use when component unmounts or user navigates away.
 *
 * @param debounceKey - The key used when calling logUserActivityDebounced
 */
export function cancelDebouncedLog(debounceKey: string): void {
  const existing = pendingLogs.get(debounceKey);
  if (existing) {
    clearTimeout(existing);
    pendingLogs.delete(debounceKey);
  }
}

/**
 * Flush a pending debounced log immediately.
 * Use when user submits a form or navigates away.
 *
 * @param debounceKey - The key used when calling logUserActivityDebounced
 */
export function flushDebouncedLog(debounceKey: string): void {
  // Note: This doesn't work with the current implementation since we don't
  // store the event. For now, just cancel — the action will be logged
  // when the user completes their workflow.
  cancelDebouncedLog(debounceKey);
}
